import type { ReactNode } from 'react';
import { ActionButton, FilterChip, Pagination, SearchBar } from '@practium/ui';
import type { TableList } from '../../hooks/useTableList';
import { pluralize } from '../../utils/format';
import { ReferenceTable, type ReferenceColumn } from './ReferenceTable';
import styles from '../Sections/Sections.module.css';

interface SectionListProps<T> {
	list: TableList<T>;
	columns: ReferenceColumn<T>[];
	getRowId: (row: T) => string;
	unit: [string, string];
	searchPlaceholder?: string;
	actionLabel?: string;
	notice?: ReactNode;
	onAction?: () => void;
	onEdit?: (row: T) => void;
	onDelete?: (row: T) => void;
}

export const SectionList = <T,>({
	list,
	columns,
	getRowId,
	unit,
	searchPlaceholder,
	actionLabel,
	notice,
	onAction,
	onEdit,
	onDelete,
}: SectionListProps<T>) => {
	const hasToolbar = list.isSearchable || list.filterGroups.length > 0 || !!(actionLabel && onAction);

	return (
		<div className={styles.section}>
			{notice}
			{hasToolbar && (
				<div className={styles.toolbar}>
					{list.isSearchable && (
						<div className={styles.search}>
							<SearchBar value={list.query} placeholder={searchPlaceholder} onChange={list.setQuery} />
						</div>
					)}
					{list.filterGroups.length > 0 && (
						<FilterChip values={list.filterValues} onChange={list.setFilterValues} groups={list.filterGroups} />
					)}
					{actionLabel && onAction && (
						<div className={styles.toolbarAction}>
							<ActionButton text={actionLabel} onClick={onAction} />
						</div>
					)}
				</div>
			)}
			<ReferenceTable
				rows={list.visible}
				columns={columns}
				getRowId={getRowId}
				emptyTitle={list.isFiltered ? 'Nenhum resultado encontrado' : 'Nada cadastrado ainda'}
				emptySubtitle={list.isFiltered ? 'Ajuste a busca ou os filtros.' : 'Os itens adicionados aparecerão aqui.'}
				onEdit={onEdit}
				onDelete={onDelete}
			/>
			<div className={styles.listFooter}>
				<span className={styles.count}>{pluralize(list.total, unit[0], unit[1])}</span>
				<Pagination page={list.page} totalPages={list.totalPages} onPageChange={list.setPage} />
			</div>
		</div>
	);
};
